import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Building07, Trash02 } from "@untitledui/icons";
import { Button, ButtonUtility, Table } from "@tricura/atlas-ui";
import { EditableCellProvider, EditableDateCell, EditableTextCell, InvalidShell } from "../../../../components/editable-cell";
import { FormGroup } from "../../../../components/form-group";
import { SelectWithCustom } from "../../../../components/select-with-custom";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { type UwInformationFormData, emptyNamedInsured } from "../schema";

const relationships = [
    { id: "parent", label: "Parent Company" },
    { id: "subsidiary", label: "Subsidiary Company" },
    { id: "affiliate", label: "Affiliate Company" },
    { id: "dba", label: "DBA" },
];

/** Column widths — applied as both `width` and `minWidth` so the
 *  Relationship dropdown never shrinks below 300 px regardless of
 *  viewport. The pattern mirrors Schedule of Location's fixed grid. */
const W = {
    relationship: 300,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });

// Sticky-column classes — split HEAD vs CELL so the table-header gray
// (bg-secondary) is preserved on TH while body cells stay white.
// Mobile (`<md`): drop the sticky pinning so the table scrolls as one
// block — the frozen actions column blanket a narrow viewport (OR-1280).
// Desktop (`md+`) unchanged. Matches Staffing Ratios.
const STICKY_HEAD = "md:sticky md:right-0 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";
const STICKY_CELL = "md:sticky md:right-0 md:bg-primary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";

export function NamedInsuredsSection() {
    const { control } = useFormContext<UwInformationFormData>();
    const { fields, prepend, remove } = useFieldArray({ control, name: "namedInsureds" });

    return (
        <FormGroup
            title="Named Insureds"
            flushBody
            action={
                <Button color="link-gray" size="sm" onClick={() => prepend(emptyNamedInsured())} data-autotest="uw-information-named-insureds-add-row-button">
                    + Add Row
                </Button>
            }
        >
            <EditableCellProvider>
                <div className="relative overflow-hidden">
                <Table
                    aria-label="Named Insureds"
                    wrapperClassName="min-h-96"
                    className="[&_tbody_tr:last-child]:border-b-0!"
                >
                    <Table.Header>
                        {/* Entity Name uses `min-w-108` (not `w-108`) so it
                          * absorbs leftover horizontal space when the card
                          * is wider than the sum of fixed columns
                          * (Relationship 300 + Retro Date 208 + Actions 80
                          * + sticky-right shadow). Without this the table
                          * leaves a visible empty stripe between Retro Date
                          * and the sticky trash icon on wide cards. */}
                        <Table.Head id="entityName" label="Entity Name" isRowHeader className="min-w-108 pl-9" />
                        <Table.Head id="relationship" label="Relationship" style={fixed(W.relationship)} />
                        <Table.Head id="retroDate" label="Retro Date" className="w-52 pl-9" />
                        <Table.Head id="actions" label="" className={`${STICKY_HEAD} w-20`} />
                    </Table.Header>
                    <Table.Body items={fields}>
                        {(row) => {
                            const idx = fields.findIndex((f) => f.id === row.id);
                            return (
                                <Table.Row id={row.id}>
                                    <Table.Cell className="min-w-108">
                                        <Controller
                                            control={control}
                                            name={`namedInsureds.${idx}.entityName`}
                                            render={({ field, fieldState }) => (
                                                <EditableTextCell
                                                    ariaLabel="Entity Name"
                                                    placeholder="Type Entity Name"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isInvalid={fieldState.invalid}
                                                    errorMessage={fieldState.error?.message}
                                                    data-autotest="uw-information-named-insured-entity-name-input"
                                                />
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell style={fixed(W.relationship)}>
                                        <Controller
                                            control={control}
                                            name={`namedInsureds.${idx}.relationship`}
                                            render={({ field, fieldState }) => (
                                                <InvalidShell isInvalid={fieldState.invalid} errorMessage={fieldState.error?.message}>
                                                    <SelectWithCustom
                                                        aria-label="Relationship"
                                                        placeholder="—"
                                                        value={field.value || ""}
                                                        onChange={field.onChange}
                                                        isInvalid={fieldState.invalid}
                                                        dataAutotestPrefix="uw-information-named-insured-relationship"
                                                        presets={relationships}
                                                        data-autotest="uw-information-named-insured-relationship-select"
                                                    />
                                                </InvalidShell>
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell className="w-52">
                                        <Controller
                                            control={control}
                                            name={`namedInsureds.${idx}.retroactiveDate`}
                                            render={({ field, fieldState }) => (
                                                <EditableDateCell
                                                    ariaLabel="Retro Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isInvalid={fieldState.invalid}
                                                    errorMessage={fieldState.error?.message}
                                                    data-autotest="uw-information-named-insured-retro-date-input"
                                                />
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell className={STICKY_CELL}>
                                        <div className="flex justify-end">
                                            <ButtonUtility
                                                color="tertiary"
                                                size="sm"
                                                icon={Trash02}
                                                tooltip="Delete row"
                                                onClick={() => remove(idx)}
                                                data-autotest="uw-information-named-insured-delete-row-button"
                                                className="hover:bg-[var(--color-utility-neutral-100)]"
                                            />
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        }}
                    </Table.Body>
                </Table>
                {fields.length === 0 && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="pointer-events-auto">
                            <TableEmptyState
                                icon={Building07}
                                title="Add Named Insureds"
                                description="Use the button below to start building the Named Insureds list"
                                onAdd={() => prepend(emptyNamedInsured())}
                                data-autotest="uw-information-named-insured-empty-add-row-button"
                            />
                        </div>
                    </div>
                )}
                {/* Faux sticky-right boundary — only visible when the
                  * table has rows. See same comment in Schedule of
                  * Location. */}
                {fields.length > 0 && (
                    <div
                        aria-hidden="true"
                        className="bg-border-secondary pointer-events-none absolute top-0 bottom-0 z-10 hidden w-px md:block"
                        style={{ right: 80 }}
                    />
                )}
                </div>
            </EditableCellProvider>
        </FormGroup>
    );
}
